import _ from '@lodash';
import useUnsavedChangesGuard from 'app/main/utils/useUnsavedChangesGuard';
import FusePageCarded from '@fuse/core/FusePageCarded';
import withReducer from 'app/store/withReducer';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import { getApiErrorMessage } from 'app/main/utils/apiErrors';
import { recipientsError } from 'app/main/utils/emailRecipients';

import reducer from '../store';
import TemplateFormHeader from './TemplateFormHeader';
import TemplateForm from './TemplateForm';

import {
  createTemplate,
  updateTemplate,
  getTemplateById,
  deleteTemplate,
  selectTemplate
} from '../store/templateSlice';

const EMPTY_FORM = {
  name: '',
  sendTo: '',
  subject: '',
  emailBody: '',
  active: false
};

// Mirrors the fields the Template schema marks required on the API.
const REQUIRED_FIELDS = [
  { key: 'name', label: 'Template name' },
  { key: 'subject', label: 'Subject' }
];

function TemplateFormPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const template = useSelector(selectTemplate);

  const [form, setForm] = useState(EMPTY_FORM);
  const [baseline, setBaseline] = useState(EMPTY_FORM);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (isEdit) {
      dispatch(getTemplateById(id));
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (template && isEdit) {
      const hydrated = {
        name: template.name || '',
        sendTo: template.sendTo || '',
        subject: template.subject || '',
        emailBody: template.emailBody || '',
        active: Boolean(template.active)
      };
      setForm(hydrated);
      setBaseline(hydrated);
    }
  }, [template, isEdit]);

  const sendToError = recipientsError(form.sendTo);
  const missingFields = REQUIRED_FIELDS.filter(field => !String(form[field.key] || '').trim());

  const skipNextBlock = useUnsavedChangesGuard({
    isDirty: !_.isEqual(form, baseline),
    canSave: !missingFields.length && !sendToError,
    saveBlockedHint:
      sendToError || `Complete ${missingFields.map(field => field.label).join(', ')} before saving.`,
    onSave: () => handleSave()
  });

  const handleSave = async () => {
    // Checked here too so the missing field is named before a round trip, and
    // so editing cannot blank out a value the schema requires.
    if (missingFields.length) {
      setSaveError(`Please complete: ${missingFields.map(field => field.label).join(', ')}.`);
      return;
    }

    if (sendToError) {
      setSaveError(sendToError);
      return;
    }

    // Redirecting unconditionally used to hide failed saves: the admin landed
    // back on the list and simply never found the template they just wrote.
    // `meta.requestStatus` only exists from redux-toolkit 1.5 and this app pins
    // 1.4, where it is always undefined — reading it reported every save as a
    // failure, successful ones included. `match` compares action types instead.
    const save = isEdit ? updateTemplate : createTemplate;
    const result = await dispatch(isEdit ? save({ id, data: form }) : save(form));

    if (!save.fulfilled.match(result)) {
      setSaveError(getApiErrorMessage(result.payload, 'The template could not be saved. Please try again.'));
      return;
    }

    skipNextBlock();
    history.push('/email-templates');
  };

  const handleDelete = async () => {
    const result = await dispatch(deleteTemplate(id));

    if (!deleteTemplate.fulfilled.match(result)) {
      setSaveError(getApiErrorMessage(result.payload, 'The template could not be deleted. Please try again.'));
      return;
    }

    skipNextBlock();
    history.push('/email-templates');
  };

  return (
    <>
      <FusePageCarded
        classes={{
          content: 'flex',
          contentCard: 'w-full max-w-xl mx-auto mb-40',
          header: 'min-h-96 h-96 sm:h-136 sm:min-h-136 text-grey-800',
          topBg: 'light-header'
        }}
        header={
          <TemplateFormHeader isEdit={isEdit} onSave={handleSave} onDelete={handleDelete} templateName={form.name} />
        }
        content={<TemplateForm form={form} setForm={setForm} />}
      />
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={6000}
        onClose={() => setSaveError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSaveError('')}>
          {saveError}
        </Alert>
      </Snackbar>
    </>
  );
}

export default withReducer('settings', reducer)(TemplateFormPage);
